import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import type { TickerNotification } from "@/api/client";
import { useT } from "@/lib/translation";

interface TickerAddedModalProps {
  open: boolean;
  notifications: TickerNotification[];
  onAcknowledge: () => void;
}

const NAME_MAX_LENGTH = 40;

function truncateName(name: string): string {
  return name.length > NAME_MAX_LENGTH
    ? `${name.slice(0, NAME_MAX_LENGTH)}…`
    : name;
}

export default function TickerAddedModal({
  open,
  notifications,
  onAcknowledge,
}: TickerAddedModalProps) {
  const t = useT();
  const count = notifications.length;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        hideClose
        className="max-w-3xl"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            {t("onboarding:tickerAdded.title", { count })}
          </DialogTitle>
          <DialogDescription>
            {t("onboarding:tickerAdded.subtitle", { count })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("onboarding:tickerAdded.body", { count })}
          </p>

          <Table
            containerClassName="max-h-[50vh] border border-border/60 rounded-md"
            className="whitespace-nowrap"
          >
            {/* Sticky within the Table's own scroll wrapper; the inset shadow stands in
                for the header border, which would scroll away with border-collapse. */}
            <TableHeader className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))] [&_tr]:border-b-0">
              <TableRow>
                <TableHead>{t("onboarding:tickerAdded.name")}</TableHead>
                <TableHead>{t("onboarding:tickerAdded.ticker")}</TableHead>
                <TableHead>{t("onboarding:tickerAdded.exchange")}</TableHead>
                <TableHead>{t("onboarding:tickerAdded.isin")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notifications.map((n) => {
                const name = n.company_name ?? n.ticker ?? "";
                const truncated = truncateName(name);
                return (
                  <TableRow key={n.id}>
                    <TableCell
                      title={truncated !== name ? name : undefined}
                      className="font-medium"
                    >
                      {truncated}
                    </TableCell>
                    <TableCell>{n.ticker ?? ""}</TableCell>
                    <TableCell>{n.exchange ?? ""}</TableCell>
                    <TableCell>{n.isin ?? ""}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button onClick={onAcknowledge} className="w-full">
            {t("onboarding:tickerAdded.gotIt")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
